import React, { useEffect } from "react";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { getAdminOverview } from "wasp/client/operations";
// AppFrame removed: route-level layout wraps admin pages
import { Card } from "../../components/ui/card";
import { SectionCards, ChartAreaInteractive } from "../../components/Admin";
import { Users, DollarSign, TrendingUp, Activity } from "lucide-react";
import type { MetricCard, ChartConfig } from "../../../types/admin";

export default function AdminOverview() {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = Boolean((user as any)?.isAdmin);
  useEffect(() => {
    if (user && !isAdmin) navigate("/");
  }, [user, isAdmin]);

  const { data, isLoading, error } = useQuery(
    getAdminOverview,
    {},
    { enabled: isAdmin },
  );

  if (error)
    return (
      <div className="p-6 text-red-600">
        Admin data unavailable: {String((error as any)?.message || error)}
      </div>
    );

  return (
      <div className="space-y-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-martinique dark:text-white">
            Admin Overview
          </h1>
          <p className="mt-2 text-martinique/70 dark:text-white/70">
            Key metrics and system status
          </p>
        </div>

        <SectionCards
          metrics={[
            {
              label: "Total Users",
              value: (data as any)?.totalUsers || 0,
              icon: <Users className="h-6 w-6" />,
              description: "Registered accounts",
            },
            {
              label: "Active Subscriptions",
              value: (data as any)?.activeSubscriptions || 0,
              icon: <Activity className="h-6 w-6" />,
              description: "Current paying users",
            },
            {
              label: "Monthly Revenue",
              value: `$${((data as any)?.revenue || 0).toLocaleString()}`,
              icon: <DollarSign className="h-6 w-6" />,
              trend: (data as any)?.revenueTrend,
            },
            {
              label: "Growth Rate",
              value: fmtPct((data as any)?.growthRate),
              icon: <TrendingUp className="h-6 w-6" />,
              trend: (data as any)?.growthTrend,
            },
          ]}
        />

        <ChartAreaInteractive
          config={{
            title: "User Growth Trend",
            description: "Daily active users over the past 30 days",
            data: (data as any)?.userGrowthData || [],
            dataKeys: ["users", "signups"],
            colors: {
              users: "hsl(var(--chart-1))",
              signups: "hsl(var(--chart-2))",
            },
          }}
        />

        <Card className="rounded-xl border-0 bg-sand p-8 shadow-card dark:bg-darkcard">
          <div className="mb-6 text-xl font-medium text-martinique dark:text-white">
            A/B Test Configuration
          </div>
          <div className="rounded-lg bg-lynx p-6 dark:bg-martinique">
            <pre className="overflow-auto whitespace-pre-wrap font-mono text-sm text-martinique/80 dark:text-white/80">
              {JSON.stringify((data as any)?.ab, null, 2)}
            </pre>
          </div>
        </Card>
      </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card className="hover:shadow-cardHover rounded-xl border-0 bg-sand p-6 shadow-card transition-all duration-200 dark:bg-darkcard">
      <div className="mb-3 text-sm font-medium text-martinique/60 dark:text-white/60">
        {label}
      </div>
      <div className="text-3xl font-bold text-martinique dark:text-white">
        {value}
      </div>
    </Card>
  );
}

function fmtPct(x?: number) {
  if (x == null || Number.isNaN(x)) return "0%";
  return `${Math.round(x * 100)}%`;
}
